/**
 * Distribution history for a single document.
 *
 * Thin wrapper over the generated orval query hook that gives consumers a
 * stable name and an `enabled` guard (skip the request until the document id
 * is known).
 */
import {
  useListDocumentDistributionsApiV1DocumentDistributionDistributionsGet,
  useGetDistributionReportApiV1DocumentDistributionReportGet,
  getListDocumentDistributionsApiV1DocumentDistributionDistributionsGetQueryKey,
} from '../../../generated/api/shared-document-distribution/shared-document-distribution';
import type { DocumentDistributionItem } from '../../../generated/api/models/documentDistributionItem';
import type { DocumentDistributionReport } from '../../../generated/api/models/documentDistributionReport';
import type { GetDistributionReportApiV1DocumentDistributionReportGetParams } from '../../../generated/api/models/getDistributionReportApiV1DocumentDistributionReportGetParams';

export type { DocumentDistributionItem, DocumentDistributionReport };

/**
 * Fetch the send history for one document (most-recent first, as ordered by
 * the backend). Disabled until `documentId` is set.
 */
export function useDocumentDistributions(documentType: string, documentId: string | undefined) {
  return useListDocumentDistributionsApiV1DocumentDistributionDistributionsGet(
    { document_type: documentType, document_id: documentId ?? '' },
    { query: { enabled: !!documentId && !!documentType } },
  );
}

/**
 * Query key for the document-distribution history list. Consumers' `distribute`
 * mutations invalidate this (no args ⇒ matches every document's history) so the
 * send log + re-share state refresh after a send.
 */
export function getDocumentDistributionsQueryKey() {
  return getListDocumentDistributionsApiV1DocumentDistributionDistributionsGetQueryKey();
}

/**
 * Fetch the tenant-wide sent-documents report (paginated, filterable by date
 * range / document type / status). Powers the sent-documents report page.
 */
export function useDocumentDistributionReport(
  params: GetDistributionReportApiV1DocumentDistributionReportGetParams,
) {
  return useGetDistributionReportApiV1DocumentDistributionReportGet(params);
}
